/**
 * Partner programs (admin-only, read-only view). Deep editing stays on the desktop backoffice
 * (docs/solutions/registry-ui.md); this screen exists so an operator can check a program's
 * platform/status/brand-count from a phone without going to a laptop.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Search, Building2 } from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { haptic } from '@/shared/utils/telegram';
import { usePrograms, useRegistryBrands } from '@/features/registry/hooks/useRegistry';

const STATUS_STYLES: Record<string, string> = {
  configured: 'bg-emerald-500/10 text-emerald-600',
  template_prepared: 'bg-blue-500/10 text-blue-600',
  needs_verification: 'bg-amber-500/10 text-amber-600',
};

export function ProgramsPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [query, setQuery] = useState('');

  const programsQuery = usePrograms(isAdmin);
  const brandsQuery = useRegistryBrands(isAdmin);

  const brandCountByProgramId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const brand of brandsQuery.data ?? []) {
      if (!brand.programId) continue;
      counts.set(brand.programId, (counts.get(brand.programId) ?? 0) + 1);
    }
    return counts;
  }, [brandsQuery.data]);

  const filtered = useMemo(() => {
    const programs = programsQuery.data ?? [];
    if (!query) return programs;
    const q = query.toLowerCase();
    return programs.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        p.platform.toLowerCase().includes(q),
    );
  }, [programsQuery.data, query]);

  const handleBack = () => {
    haptic.impact('light');
    navigate(-1);
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-4 pb-20">
        <Header onBack={handleBack} />
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <AlertCircle size={40} className="text-[var(--tg-theme-hint-color)]" />
          <p className="text-sm font-bold text-[var(--tg-theme-hint-color)]">
            Admin access required
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-24 animate-in fade-in slide-in-from-right duration-300">
      <Header onBack={handleBack} />

      <div className="relative">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <Search size={14} className="text-[var(--tg-theme-hint-color)]" />
        </div>
        <input
          type="text"
          placeholder="Search programs..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full h-11 pl-10 pr-4 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20 placeholder:opacity-50"
        />
      </div>

      {programsQuery.isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--tg-theme-link-color)] border-t-transparent" />
        </div>
      )}

      {programsQuery.error && (
        <div className="p-6 mx-1 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-center">
          <p className="text-sm font-bold text-rose-600">Failed to load programs</p>
        </div>
      )}

      {!programsQuery.isLoading && !programsQuery.error && filtered.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm font-bold text-[var(--tg-theme-hint-color)] uppercase">
            No programs found
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {filtered.map((program) => (
          <div
            key={program.id}
            className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] border border-black/5 dark:border-white/5"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-bold text-sm text-[var(--tg-theme-text-color)] truncate">
                  {program.name}
                </h3>
                <p className="text-[11px] text-[var(--tg-theme-hint-color)] font-mono truncate">
                  {program.code}
                </p>
              </div>
              <span
                className={`flex-shrink-0 text-[10px] font-black uppercase px-2 py-1 rounded-full ${STATUS_STYLES[program.status] ?? 'bg-black/5 dark:bg-white/10 text-[var(--tg-theme-hint-color)]'}`}
              >
                {program.status.replace(/_/g, ' ')}
              </span>
            </div>

            <div className="flex items-center gap-2 mt-3">
              <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-[var(--tg-theme-link-color)]/10 text-[var(--tg-theme-link-color)]">
                {program.platform}
              </span>
              <span className="flex items-center gap-1 text-[11px] text-[var(--tg-theme-hint-color)] font-medium">
                <Building2 size={12} />
                {brandCountByProgramId.get(program.id) ?? 0} brand
                {(brandCountByProgramId.get(program.id) ?? 0) === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Header({ onBack }: { onBack: () => void }) {
  return (
    <div className="sticky top-0 z-10 bg-[var(--tg-theme-bg-color)] pt-2 pb-2 flex items-center gap-3">
      <button
        onClick={onBack}
        className="p-2 rounded-full hover:bg-[var(--tg-theme-secondary-bg-color)] transition-colors"
        aria-label="Back"
      >
        <ArrowLeft size={20} className="text-[var(--tg-theme-hint-color)]" />
      </button>
      <h1 className="text-2xl font-black uppercase tracking-tighter">Partner Programs</h1>
    </div>
  );
}
